import { Routes } from '@angular/router';
import { authGuard, guestGuard } from './guards/auth.guard';
import { featureGuard } from './guards/feature.guard';

export const routes: Routes = [
  { 
    path: 'old-landing',
    loadComponent: () => import('./components/landing/landing.component').then(c => c.LandingComponent),
    pathMatch: 'full' 
  },
  {
    path: '',
    loadComponent: () => import('./components/new-landing/new-landing.component').then(c => c.NewLandingComponent)
  },
  {
    path: 'google-analytics-alternatives',
    loadComponent: () =>
      import('./components/landing-google-analytics-alternatives/google-analytics-alternatives.component').then(
        c => c.GoogleAnalyticsAlternativesComponent
      )
  },
  {
    path: 'pricing',
    loadComponent: () => import('./components/pricing/pricing.component').then(c => c.PricingComponent)
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('./components/privacy-policy/privacy-policy.component').then(c => c.PrivacyPolicyComponent)
  },
  {
    path: 'terms',
    loadComponent: () => import('./components/terms/terms.component').then(c => c.TermsComponent)
  },
  {
    path: 'refund-policy',
    loadComponent: () => import('./components/refund-policy/refund-policy.component').then(c => c.RefundPolicyComponent)
  },
  
  // Direct access to dashboard for authenticated users
  { 
    path: 'app', 
    redirectTo: '/dashboard', 
    pathMatch: 'full' 
  },
  
  // Authentication routes (for non-authenticated users)
  { 
    path: 'auth/login', 
    loadComponent: () => import('./components/auth/login/login.component').then(c => c.LoginComponent),
    canActivate: [guestGuard]
  },
  { 
    path: 'auth/signup', 
    loadComponent: () => import('./components/auth/signup/signup.component').then(c => c.SignupComponent),
    canActivate: [guestGuard]
  },
  { 
    path: 'auth/magic-link', 
    loadComponent: () => import('./components/auth/magic-link/magic-link.component').then(c => c.MagicLinkComponent),
    canActivate: [guestGuard]
  },
  { 
    path: 'auth/verify-magic-link', 
    loadComponent: () => import('./components/auth/magic-link-verify/magic-link-verify.component').then(c => c.MagicLinkVerifyComponent),
    canActivate: [guestGuard]
  },
  { 
    path: 'auth/reset', 
    loadComponent: () => import('./components/auth/forgot-password/forgot-password.component').then(c => c.ForgotPasswordComponent),
    canActivate: [guestGuard]
  },
  { 
    path: 'auth/reset-password', 
    loadComponent: () => import('./components/auth/reset-password/reset-password.component').then(c => c.ResetPasswordComponent),
    canActivate: [guestGuard]
  },
  
  // Protected routes (require authentication)
  { 
    path: '', 
    loadComponent: () => import('./components/layout/layout.component').then(c => c.LayoutComponent),
    canActivate: [authGuard],
    children: [
      {
        path: 'setup',
        loadComponent: () => import('./components/user/site-setup-wizard/site-setup-wizard.component').then(c => c.SiteSetupWizardComponent)
      },
      { 
        path: 'dashboard', 
        loadComponent: () => import('./components/dashboards/dashboard/dashboard.component').then(c => c.DashboardComponent)
      },
      {
        path: 'advanced-dashboard',
        loadComponent: () => import('./components/dashboards/advanced-dashboard/advanced-dashboard.component').then(c => c.AdvancedDashboardComponent),
        canActivate: [featureGuard],
        data: { requiredFeature: 'advanced_dashboard', upgradeUrl: '/pricing' }
      },
      { 
        path: 'sites', 
        loadComponent: () => import('./components/sites/sites.component').then(c => c.SitesComponent) 
      },
      { 
        path: 'events', 
        loadComponent: () => import('./components/events/events.component').then(c => c.EventsComponent) 
      },
      { 
        path: 'org', 
        loadComponent: () => import('./components/organization/organization.component').then(c => c.OrganizationComponent) 
      },
      {
        path: 'docs',
        loadComponent: () => import('./components/docs/docs.component').then(c => c.DocsComponent)
      },
      {
        path: 'settings/excluded-ips',
        loadComponent: () => import('./components/project-settings/excluded-ips/excluded-ips.component').then(c => c.ExcludedIpsComponent)
      },
      {
        path: 'settings/custom-cards',
        loadComponent: () => import('./components/project-settings/custom-cards-management/custom-cards-management.component').then(c => c.CustomCardsManagementComponent),
        canActivate: [featureGuard],
        data: { requiredFeature: 'custom_dashboard_cards', upgradeUrl: '/pricing' }
      },
      {
        path: 'settings',
        loadComponent: () => import('./components/user/settings/user-settings.component').then(c => c.UserSettingsComponent)
      },
      {
        path: 'analytics/ecommerce',
        loadComponent: () => import('./components/ecommerce-analytics/ecommerce-analytics.component').then(c => c.EcommerceAnalyticsComponent),
        canActivate: [featureGuard],
        data: { requiredFeature: 'specialized_analytics', upgradeUrl: '/pricing' }
      },
      {
        path: 'analytics/saas',
        loadComponent: () => import('./components/saas-metrics/saas-metrics.component').then(c => c.SaasMetricsComponent),
        canActivate: [featureGuard],
        data: { requiredFeature: 'specialized_analytics', upgradeUrl: '/pricing' }
      },
      {
        path: 'analytics/content',
        loadComponent: () => import('./components/content-analytics/content-analytics.component').then(c => c.ContentAnalyticsComponent),
        canActivate: [featureGuard],
        data: { requiredFeature: 'specialized_analytics', upgradeUrl: '/pricing' }
      },
      {
        path: 'analytics/social',
        loadComponent: () => import('./components/social-analytics/social-analytics.component').then(c => c.SocialAnalyticsComponent),
        canActivate: [featureGuard],
        data: { requiredFeature: 'specialized_analytics', upgradeUrl: '/pricing' }
      },
      {
        path: 'analytics/email',
        loadComponent: () => import('./components/email-analytics/email-analytics.component').then(c => c.EmailAnalyticsComponent),
        canActivate: [featureGuard],
        data: { requiredFeature: 'specialized_analytics', upgradeUrl: '/pricing' }
      },
      {
        path: 'analytics/funnel',
        loadComponent: () => import('./components/funnel-analysis/funnel-analysis.component').then(c => c.FunnelAnalysisComponent),
        canActivate: [featureGuard],
        data: { requiredFeature: 'funnel_analysis', upgradeUrl: '/pricing' }
      },
      {
        path: 'metrics/:metric',
        loadComponent: () => import('./components/metric-detail/metric-detail.component').then(c => c.MetricDetailComponent)
      },
      {
        path: 'query-builder',
        loadComponent: () => import('./components/query-builder/query-builder.component').then(c => c.QueryBuilderComponent),
        canActivate: [featureGuard],
        data: { requiredFeature: 'query_builder', upgradeUrl: '/pricing' }
      },
      {
        path: 'compare',
        loadComponent: () => import('./components/compare/compare.component').then(c => c.CompareComponent)
      },
      {
        path: 'advanced/cohort-analysis',
        loadComponent: () => import('./components/cohort-analysis/cohort-analysis-page.component').then(c => c.CohortAnalysisPageComponent),
        canActivate: [featureGuard],
        data: { requiredFeature: 'cohort_analysis', upgradeUrl: '/pricing' }
      },
      {
        path: 'advanced/user-journey',
        loadComponent: () => import('./components/user-journey/user-journey.component').then(c => c.UserJourneyComponent),
        canActivate: [featureGuard],
        data: { requiredFeature: 'user_journey', upgradeUrl: '/pricing' }
      },
      {
        path: 'bot-analytics',
        loadComponent: () => import('./components/bot-analytics/bot-analytics.component').then(c => c.BotAnalyticsComponent)
      },
      {
        path: 'bot-events',
        loadComponent: () => import('./components/bot-events/bot-events.component').then(c => c.BotEventsComponent)
      }
    ]
  },
  
  { path: '**', redirectTo: '/dashboard' }
];
